/**
 * Utilidades para el manejo de imágenes médicas
 */

/**
 * Función para formatear fechas de imágenes
 * @param {Date} date - La fecha a formatear
 * @returns {string} La fecha formateada
 */
export function formatImagingDate(date: Date): string {
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Función para formatear el tamaño de archivo
 * @param {number} bytes - Tamaño en bytes
 * @returns {string} Tamaño formateado
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Función para obtener la variante del badge de modalidad
 * @param {string} modality - La modalidad de la imagen
 * @returns {string} La variante del badge
 */
export function getModalityBadgeVariant(
  modality: string
): "default" | "secondary" | "destructive" | "outline" {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    CT: "default",
    MRI: "secondary",
    "X-RAY": "outline",
    ULTRASOUND: "outline",
    PET: "destructive",
    SPECT: "destructive",
  };
  return variants[modality.toUpperCase()] ?? "outline";
}

/**
 * Función para obtener la variante del badge de parte del cuerpo
 * @param {string} bodyPart - La parte del cuerpo
 * @returns {string} La variante del badge
 */
export function getBodyPartBadgeVariant(
  bodyPart: string
): "default" | "secondary" | "destructive" | "outline" {
  if (!bodyPart) return "outline";
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    HEAD: "default",
    CHEST: "secondary",
    ABDOMEN: "outline",
    SPINE: "destructive",
    EXTREMITIES: "outline",
  };
  return variants[bodyPart.toUpperCase()] ?? "outline";
}

/**
 * Función para truncar texto largo
 * @param {string} text - El texto a truncar
 * @param {number} maxLength - La longitud máxima
 * @returns {string} El texto truncado
 */
export function truncateText(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/**
 * Función para obtener el estado de la imagen basado en la fecha
 * @param {Date} date - La fecha de la imagen
 * @returns {string} El estado de la imagen
 */
export function getImagingStatus(date: Date): "recent" | "old" | "very-old" {
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays <= 7) return "recent";
  if (diffInDays <= 30) return "old";
  return "very-old";
}

/**
 * Función para obtener el color del estado de la imagen
 * @param {string} status - El estado de la imagen
 * @returns {string} El color del estado
 */
export function getImagingStatusColor(status: "recent" | "old" | "very-old"): string {
  const colors = {
    recent: "text-green-600 dark:text-green-400",
    old: "text-yellow-600 dark:text-yellow-400",
    "very-old": "text-red-600 dark:text-red-400",
  };
  return colors[status];
}
